import {
  matchesSearch,
  queueSearchText,
  queueStatusText,
} from "./releaseSearch.ts";
import type { QueueItem, QueueState } from "../api/migrationQueue";

export const isQueueHistory = (item: Pick<QueueItem, "status">) =>
  ["applied", "cancelled"].includes(item.status);
const normalize = (text: string) => text.normalize("NFKC").trim().toLowerCase();
export function queueMatchRank(query: string, item: QueueItem) {
  const q = normalize(query);
  if (!q) return 1;
  const fields = [
    item.version,
    item.name,
    item.file_path ?? `supabase/migrations/${item.version}_${item.name}.sql`,
    item.sql_sha256 ?? "",
  ]
    .flatMap(
      (value) => [normalize(value), normalize(value.split("/").pop() ?? value)],
    );
  if (fields.some((value) => value === q)) return 3;
  if (fields.some((value) => value.startsWith(q))) return 2;
  return matchesSearch(q, queueSearchText(item)) ? 1 : 0;
}
export function searchQueueItems<T extends QueueItem>(
  items: T[],
  query: string,
): T[] {
  return items.map((item, index) => ({
    item,
    index,
    rank: queueMatchRank(query, item),
  }))
    .filter((row) => row.rank > 0).sort((a, b) =>
      b.rank - a.rank ||
      Number(isQueueHistory(a.item)) - Number(isQueueHistory(b.item)) ||
      a.index - b.index
    )
    .map((row) => row.item);
}
export function queueResultCount(items: QueueItem[]) {
  const history = items.filter(isQueueHistory).length;
  return `${items.length} תוצאות (${
    items.length - history
  } פעילים, ${history} היסטוריה)`;
}
export function queueStatusLine(item: QueueItem) {
  const date = item.status === "applied"
    ? item.applied_at
    : item.status === "cancelled"
    ? item.cancelled_at
    : null;
  if (!date || !Number.isFinite(Date.parse(date))) {
    return queueStatusText(item.status);
  }
  const parts = new Intl.DateTimeFormat("he-IL", {
    timeZone: "Asia/Jerusalem",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(date));
  const part = (key: string) => parts.find((p) => p.type === key)?.value;
  return `${queueStatusText(item.status)} ב־${part("day")}.${part("month")} ${
    part("hour")
  }:${part("minute")}`;
}
export async function prepareQueueSearch<T extends QueueItem>(
  item: T,
): Promise<T> {
  let hash = item.sql_sha256;
  if (!hash && typeof item.sql === "string") {
    hash = [
      ...new Uint8Array(
        await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(item.sql),
        ),
      ),
    ].map((n) => n.toString(16).padStart(2, "0")).join("");
  }
  return {
    ...item,
    file_path: item.file_path ??
      `supabase/migrations/${item.version}_${item.name}.sql`,
    sql_sha256: hash,
  };
}

// Hashes are display/search metadata, never authorization receipts. Poll only
// guarded metadata; do not retain/re-download the historical SQL corpus.
export function createQueueSearchLoader(
  read: (id?: string | null, includeSql?: boolean) => Promise<QueueState>,
) {
  const cache = new Map<string, { file_path?: string; sql_sha256?: string }>();
  const key = (item: QueueItem) =>
    JSON.stringify([
      item.repo,
      item.id,
      item.version,
      item.name,
      item.git_sha ?? "",
      item.sql_sha256 ?? "",
    ]);
  let hydrated = false;
  let hydration: Promise<void> | null = null;
  async function remember(item: QueueItem) {
    const row = await prepareQueueSearch(item);
    if (!row.sql_sha256) throw Error("SQL search hash unavailable");
    cache.set(key(item), {
      file_path: row.file_path,
      sql_sha256: row.sql_sha256,
    });
  }
  async function load() {
    const metadata = await read();
    if (!hydrated && metadata.items.some((item) => !cache.has(key(item)))) {
      // Share only non-sensitive hash hydration, never another caller's rows.
      // Every caller independently reads/reauthorizes its own metadata first.
      if (!hydration) {
        hydration = (async () => {
          const first = await read(null, true);
          await Promise.all(first.items.map(remember));
          hydrated = true;
        })().finally(() => {
          hydration = null;
        });
      }
      await hydration;
    }
    const missing = metadata.items.filter((item) => !cache.has(key(item)));
    for (let start = 0; start < missing.length; start += 8) {
      await Promise.all(
        missing.slice(start, start + 8).map(async (item) => {
          const result = await read(item.id, true),
            row = result.items.find((row) => key(row) === key(item));
          if (!row) {
            throw Error("Queue search identity changed; refresh required");
          }
          await remember(row);
        }),
      );
    }
    return {
      ...metadata,
      items: metadata.items.map((item) => ({
        ...item,
        ...cache.get(key(item)),
        sql: null,
      })),
    };
  }
  return load;
}
